import { QRCodeSVG } from 'qrcode.react';
import type { TemplateData } from './template-types';
import { TIPO_LABEL, SECCION_LABEL } from './template-types';

const SIDEBAR_BG  = '#1e40af';
const SIDEBAR_W   = 166;
const ACCENT      = '#3b82f6';

function QRBox({ url }: { url?: string }) {
  if (url) {
    return (
      <div style={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <QRCodeSVG value={url} size={64} fgColor="rgba(255,255,255,0.92)" bgColor="transparent" />
      </div>
    );
  }
  return (
    <div style={{ width: 72, height: 72, border: '1.5px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg viewBox="0 0 21 21" width="64" height="64">
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => { const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]]; return p[r]?.[c] ? <rect key={`a-${r}-${c}`} x={c*3} y={r*3} width={3} height={3} fill="#fff" opacity={0.9} /> : null; }))}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => { const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]]; return p[r]?.[c] ? <rect key={`b-${r}-${c}`} x={(14+c)*3} y={r*3} width={3} height={3} fill="#fff" opacity={0.9} /> : null; }))}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => { const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]]; return p[r]?.[c] ? <rect key={`c-${r}-${c}`} x={c*3} y={(14+r)*3} width={3} height={3} fill="#fff" opacity={0.9} /> : null; }))}
      </svg>
    </div>
  );
}

function Contenido({ data }: { data: TemplateData }) {
  if (data.tipo === 'receta') return (
    <>
      {data.medicaciones?.map((m, i) => (
        <div key={i} style={{ padding: '9px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#1a1a1a' }}>{i + 1}. {m.denominacionGenerica}{m.denominacionDistintiva ? <span style={{ fontWeight: 400, color: '#666', fontSize: 11 }}> ({m.denominacionDistintiva})</span> : ''}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 3, fontSize: 10.5, color: '#555' }}>
            {m.dosis             && <span>Dosis: <b>{m.dosis}</b></span>}
            {m.frecuencia        && <span>Frecuencia: <b>{m.frecuencia}</b></span>}
            {m.duracion          && <span>Duración: <b>{m.duracion}</b></span>}
            {m.viaAdministracion && <span>Vía: <b>{m.viaAdministracion}</b></span>}
          </div>
          {m.indicaciones && <div style={{ marginTop: 2, fontSize: 10, color: '#888', fontStyle: 'italic' }}>{m.indicaciones}</div>}
        </div>
      ))}
      {(!data.medicaciones?.length) && <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: 11 }}>No se incluyeron medicamentos.</p>}
    </>
  );
  if (data.tipo === 'orden') return (
    <div style={{ fontSize: 12, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
      {data.instrucciones || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin indicaciones.</span>}
    </div>
  );
  if (data.tipo === 'presupuesto') {
    const items    = data.presupuestoItems ?? [];
    const moneda   = data.presupuestoMoneda ?? 'MXN';
    const subtotal = items.reduce((s, i) => s + i.cantidad * i.precioUnitario, 0);
    const descPct  = data.presupuestoDescuento ?? 0;
    const descAmt  = subtotal * (descPct / 100);
    const ivaPct   = data.presupuestoIva ?? 0;
    const ivaAmt   = (subtotal - descAmt) * (ivaPct / 100);
    const total    = subtotal - descAmt + ivaAmt;
    const fmt      = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 10px', padding: '5px 0', borderBottom: `2px solid ${SIDEBAR_BG}`, fontSize: 9.5, fontWeight: 700, color: SIDEBAR_BG, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <span>Servicio</span>
          <span style={{ textAlign: 'right', minWidth: 28 }}>Cant.</span>
          <span style={{ textAlign: 'right', minWidth: 68 }}>Precio u.</span>
          <span style={{ textAlign: 'right', minWidth: 76 }}>Total</span>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 10px', padding: '7px 0', borderBottom: '1px solid #f0f0f0', fontSize: 11, alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{item.servicio}</div>
              {item.descripcion && <div style={{ fontSize: 9.5, color: '#888' }}>{item.descripcion}</div>}
            </div>
            <span style={{ textAlign: 'right', color: '#777', minWidth: 28 }}>{item.cantidad}</span>
            <span style={{ textAlign: 'right', minWidth: 68 }}>{fmt(item.precioUnitario)}</span>
            <span style={{ textAlign: 'right', fontWeight: 600, minWidth: 76 }}>{fmt(item.cantidad * item.precioUnitario)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, marginTop: 10, fontSize: 11 }}>
          <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#888' }}>Subtotal</span><span style={{ minWidth: 76, textAlign: 'right' }}>{fmt(subtotal)}</span></div>
          {descPct > 0 && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#888' }}>Descuento ({descPct}%)</span><span style={{ minWidth: 76, textAlign: 'right', color: '#16a34a' }}>−{fmt(descAmt)}</span></div>}
          {ivaPct > 0  && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#888' }}>IVA ({ivaPct}%)</span><span style={{ minWidth: 76, textAlign: 'right' }}>{fmt(ivaAmt)}</span></div>}
          <div style={{ display: 'flex', gap: 16, background: `${SIDEBAR_BG}15`, borderLeft: `3px solid ${SIDEBAR_BG}`, paddingLeft: 8, paddingRight: 4, paddingTop: 4, paddingBottom: 4, marginTop: 2 }}>
            <span style={{ fontWeight: 700 }}>TOTAL {moneda}</span>
            <span style={{ minWidth: 76, textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(total)}</span>
          </div>
          {data.presupuestoValidezDias && <div style={{ fontSize: 9.5, color: '#888', marginTop: 4 }}>Vigencia: {data.presupuestoValidezDias} días</div>}
        </div>
      </div>
    );
  }
  if (data.tipo === 'plan-tratamiento') {
    const items = data.planTratamientoItems ?? [];
    return (
      <div>
        {(data.diagnostico || data.objetivoTratamiento) && (
          <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 10, marginBottom: 12, fontSize: 11 }}>
            {data.diagnostico        && <div><span style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Diagnóstico</span><br /><b>{data.diagnostico}</b></div>}
            {data.objetivoTratamiento && <div style={{ marginTop: 6 }}><span style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Objetivo</span><br /><b>{data.objetivoTratamiento}</b></div>}
          </div>
        )}
        {items.map((item, i) => (
          <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{i + 1}. {item.tratamiento}</div>
            {item.descripcion && <div style={{ fontSize: 10.5, color: '#555', marginTop: 2 }}>{item.descripcion}</div>}
            <div style={{ display: 'flex', gap: 16, marginTop: 3, fontSize: 10, color: '#888' }}>
              {item.sesiones !== undefined && <span>Sesiones: <b>{item.sesiones}</b></span>}
              {item.duracion               && <span>Duración: <b>{item.duracion}</b></span>}
            </div>
          </div>
        ))}
        {(data.duracionTratamiento || data.frecuenciaTratamiento) && (
          <div style={{ marginTop: 10, padding: '6px 10px', background: `${ACCENT}15`, borderLeft: `3px solid ${ACCENT}`, fontSize: 10.5, color: SIDEBAR_BG }}>
            {data.duracionTratamiento  && <span><b>Duración total:</b> {data.duracionTratamiento}</span>}
            {data.duracionTratamiento && data.frecuenciaTratamiento && <span style={{ margin: '0 10px', opacity: 0.3 }}>│</span>}
            {data.frecuenciaTratamiento && <span><b>Frecuencia:</b> {data.frecuenciaTratamiento}</span>}
          </div>
        )}
      </div>
    );
  }
  if (data.tipo === 'consentimiento') return (
    <div>
      {data.consentimientoTitulo && <div style={{ fontWeight: 700, fontSize: 12.5, color: SIDEBAR_BG, marginBottom: 10 }}>{data.consentimientoTitulo}</div>}
      <div style={{ fontSize: 11.5, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
        {data.consentimientoContenido || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin contenido.</span>}
      </div>
    </div>
  );
  return (
    <>
      {data.estudios?.map((e, i) => (
        <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 600 }}>{i + 1}. {e.nombre}</div>
          {e.descripcion && <div style={{ fontSize: 10.5, color: '#777' }}>{e.descripcion}</div>}
        </div>
      ))}
    </>
  );
}

function FirmaPacienteBox({ data }: { data: TemplateData }) {
  if (data.tipo !== 'consentimiento') return null;
  return (
    <div style={{ textAlign: 'center' }}>
      {data.firmaUrl
        ? (data.firmaEsPdf
          ? <div style={{ fontSize: 9.5, color: '#888', fontStyle: 'italic', width: 200 }}>Documento firmado adjunto (PDF)</div>
          : <img src={data.firmaUrl} alt="Firma del paciente" style={{ height: 50, maxWidth: 200, objectFit: 'contain' }} />)
        : <div style={{ height: 50, width: 200 }} />}
      <div style={{ borderTop: '1.5px solid #333', width: 200, marginTop: 6, marginBottom: 6 }} />
      <div style={{ fontSize: 10.5, fontWeight: 600 }}>{data.paciente.nombre}</div>
      <div style={{ fontSize: 9.5, color: '#888' }}>Firma del paciente</div>
    </div>
  );
}

export function TemplateBarra({ data, width = 794, height = 1123 }: { data: TemplateData; width?: number; height?: number }) {
  const tituloDoc = TIPO_LABEL[data.tipo];
  return (
    <div style={{ width, height, fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 11, color: '#1a1a1a', backgroundColor: '#fff', display: 'flex', boxSizing: 'border-box' }}>

      {/* Sidebar */}
      <div style={{ width: SIDEBAR_W, minHeight: '100%', background: SIDEBAR_BG, color: '#fff', display: 'flex', flexDirection: 'column', padding: '28px 16px', flexShrink: 0, gap: 0 }}>
        {/* Logo */}
        <div style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, border: '1.5px solid rgba(255,255,255,0.25)', alignSelf: 'center' }}>
          {data.clinica.logoUrl
            ? <img src={data.clinica.logoUrl} alt="" style={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 8 }} />
            : <span style={{ fontSize: 24, fontWeight: 900, color: '#fff' }}>{data.clinica.nombre.charAt(0)}</span>}
        </div>

        {/* Clinic name */}
        <div style={{ fontWeight: 700, fontSize: 12, textAlign: 'center', letterSpacing: 0.3, lineHeight: 1.3, marginBottom: 6 }}>
          {data.clinica.nombre}
        </div>
        {data.clinica.cedula && (
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.65)', textAlign: 'center', marginBottom: 16 }}>
            {data.clinica.cedula}
          </div>
        )}

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginBottom: 16 }} />

        {/* Doctor info */}
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>Médico</div>
        <div style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3, marginBottom: 4 }}>{data.medico.nombre}</div>
        {data.medico.especialidad && (
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{data.medico.especialidad}</div>
        )}
        {data.medico.cedula && (
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.55)', marginBottom: 16 }}>Ced. {data.medico.cedula}</div>
        )}

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginBottom: 16 }} />

        {/* Address */}
        {data.clinica.direccion && (
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginBottom: 8 }}>{data.clinica.direccion}</div>
        )}
        {data.clinica.telefono && (
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.6)' }}>Tel. {data.clinica.telefono}</div>
        )}

        {/* QR at bottom of sidebar */}
        <div style={{ marginTop: 'auto', paddingTop: 24, alignSelf: 'center' }}>
          <QRBox url={data.qrUrl} />
          <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginTop: 4 }}>Verificar documento</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: '28px 32px 24px', display: 'flex', flexDirection: 'column' }}>

        {/* Document header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: SIDEBAR_BG }}>{tituloDoc}</div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{data.fecha}</div>
          </div>
          <div style={{ background: '#f0f4ff', border: `1px solid ${ACCENT}33`, borderRadius: 8, padding: '6px 14px', textAlign: 'right' }}>
            <div style={{ fontSize: 9.5, color: '#777' }}>Folio</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: SIDEBAR_BG }}>{data.folio}</div>
          </div>
        </div>

        <div style={{ borderTop: '2px solid ' + SIDEBAR_BG, marginBottom: 16, opacity: 0.15 }} />

        {/* Patient info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 20, rowGap: 10, marginBottom: 18, fontSize: 11 }}>
          <div>
            <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Paciente</div>
            <div style={{ fontWeight: 700 }}>{data.paciente.nombre}</div>
          </div>
          <div>
            <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Fecha de nacimiento</div>
            <div style={{ fontWeight: 600 }}>{data.paciente.fechaNacimiento ?? '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Sexo</div>
            <div style={{ fontWeight: 600 }}>{data.paciente.sexo ?? '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>Fecha de consulta</div>
            <div style={{ fontWeight: 600 }}>{data.fechaConsulta ?? '—'}</div>
          </div>
        </div>

        {/* Section title */}
        <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 10, fontWeight: 700, fontSize: 12, color: SIDEBAR_BG, marginBottom: 12 }}>
          {SECCION_LABEL[data.tipo]}
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <Contenido data={data} />
        </div>

        {/* Signature(s) */}
        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end', gap: 32 }}>
          <FirmaPacienteBox data={data} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ borderTop: '1.5px solid #333', width: 200, marginBottom: 6 }} />
            <div style={{ fontSize: 10.5, fontWeight: 600 }}>{data.medico.nombre}</div>
            {data.medico.cedula && <div style={{ fontSize: 9.5, color: '#888' }}>Cédula: {data.medico.cedula}</div>}
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 16, paddingTop: 8, fontSize: 9, color: '#aaa', textAlign: 'center' }}>
          Receta electrónica — {data.clinica.nombre}
        </div>
      </div>
    </div>
  );
}
